import { useEffect, useState } from "react";
import { Download, Smartphone, X, Share, Plus, MoreVertical } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const HIDE_KEY = "ndis_install_dismissed";

/**
 * "Install app" button + dialog.
 *
 * Behaviour:
 *  - On Android Chrome / desktop Chrome / Edge: listens for `beforeinstallprompt`
 *    and triggers the native install prompt on click.
 *  - On iOS Safari: no programmatic install API exists, so we show a step-by-step
 *    overlay describing how to "Add to Home Screen".
 *  - Hidden when the app is already installed (display-mode: standalone) or the
 *    user has dismissed it.
 */
export default function InstallAppButton({ variant = "sidebar" }) {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showIOS, setShowIOS] = useState(false);
  const [installed, setInstalled] = useState(false);
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(HIDE_KEY) === "1");

  useEffect(() => {
    // Already installed / running standalone — hide the CTA entirely.
    const standalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    if (standalone) setInstalled(true);

    const onBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onAppInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    window.addEventListener("appinstalled", onAppInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
      window.removeEventListener("appinstalled", onAppInstalled);
    };
  }, []);

  if (installed || dismissed) return null;

  const isIOS =
    /iPad|iPhone|iPod/.test(navigator.userAgent) &&
    !window.MSStream;

  const handleClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === "accepted") setInstalled(true);
      setDeferredPrompt(null);
      return;
    }
    if (isIOS) {
      setShowIOS(true);
      return;
    }
    // Browsers without install API (e.g., Firefox desktop) — fall back to instructions.
    setShowIOS(true);
  };

  // Don't show the button at all if no install path is available
  // and the user is on desktop Chrome / Edge waiting for the heuristic
  // (engagement gates) to pass. We still show on iOS because that path
  // is purely instructional.
  if (!deferredPrompt && !isIOS && variant === "sidebar") {
    // Show as a soft hint button so it's discoverable; click triggers fallback.
  }

  if (variant === "compact") {
    return (
      <>
        <Button
          size="sm"
          variant="outline"
          onClick={handleClick}
          data-testid="install-app-btn-compact"
          className="rounded-full font-bold gap-1.5 border-2 h-8 px-3 text-xs"
        >
          <Download className="w-3.5 h-3.5" /> Install app
        </Button>
        <IOSInstructionsDialog open={showIOS} onClose={() => setShowIOS(false)} />
      </>
    );
  }

  return (
    <>
      <div
        data-testid="install-app-card"
        className="rounded-2xl border-2 border-client/30 bg-client/8 p-3 mx-2 my-2 relative"
      >
        <button
          onClick={() => { localStorage.setItem(HIDE_KEY, "1"); setDismissed(true); }}
          aria-label="Dismiss install"
          data-testid="install-app-dismiss"
          className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full grid place-items-center text-muted-foreground hover:bg-muted/60"
        >
          <X className="w-3 h-3" />
        </button>
        <div className="flex items-center gap-2 mb-1.5">
          <Smartphone className="w-4 h-4 text-client" />
          <p className="text-xs font-bold">Install on your device</p>
        </div>
        <p className="text-[11px] text-muted-foreground mb-2 leading-snug">
          One-tap access. Works offline.
        </p>
        <Button
          size="sm"
          onClick={handleClick}
          data-testid="install-app-btn"
          className="w-full rounded-full font-bold gap-1.5 text-xs text-white h-8"
          style={{ background: "hsl(var(--client))" }}
        >
          <Download className="w-3.5 h-3.5" />
          Install
        </Button>
      </div>
      <IOSInstructionsDialog open={showIOS} onClose={() => setShowIOS(false)} />
    </>
  );
}


function IOSInstructionsDialog({ open, onClose }) {
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="rounded-3xl max-w-md" data-testid="install-instructions-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-display font-black tracking-tight flex items-center gap-2">
            <Smartphone className="w-5 h-5 text-client" />
            {isIOS ? "Add to your iPhone home screen" : "Install on this device"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          {isIOS ? (
            <ol className="space-y-3">
              <Step n={1}>
                Tap the <b className="inline-flex items-center gap-1"><Share className="w-4 h-4 text-client" /> Share</b> icon at the bottom of Safari.
              </Step>
              <Step n={2}>
                Scroll down and tap <b className="inline-flex items-center gap-1"><Plus className="w-4 h-4 text-client" /> Add to Home Screen</b>.
              </Step>
              <Step n={3}>
                Tap <b>Add</b> in the top-right. The hub now lives on your home screen with its own icon.
              </Step>
            </ol>
          ) : (
            <ol className="space-y-3">
              <Step n={1}>
                Click the <b className="inline-flex items-center gap-1"><MoreVertical className="w-4 h-4 text-client" /> three-dot menu</b> at the top-right of your browser.
              </Step>
              <Step n={2}>
                Choose <b>Install app</b> or <b>Apps → Install this site as an app</b>.
              </Step>
              <Step n={3}>
                Confirm. The hub opens in its own window — no browser tabs.
              </Step>
              <p className="text-xs text-muted-foreground pt-1">
                On Safari (Mac): use <b>File → Add to Dock</b>. On Firefox: install via your add-ons or use the desktop site.
              </p>
            </ol>
          )}
          <Button onClick={onClose} className="w-full rounded-full font-bold mt-2" data-testid="install-instructions-close">
            Got it
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


function Step({ n, children }) {
  return (
    <li className="flex gap-3 text-sm leading-relaxed">
      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-client text-white grid place-items-center text-xs font-black">
        {n}
      </span>
      <span className="flex-1 pt-0.5">{children}</span>
    </li>
  );
}
